import { logger } from '@/utils/logger';

/**
 * Utility functions to parse the workflow JSON and convert it to a graph structure
 * Updated to handle all reference types equally and use explicit handle IDs for edges
 * Enhanced to identify action frames based on dataframe patterns
 * Improved to assign more specific node types according to their content
 */

class WorkflowParser {
  constructor(showSnackbar) {
    this.showSnackbar = showSnackbar;
  }
  /**
   * Parse the JSON workflow into nodes and edges for react-flow
   * @param {Object} json - The parsed JSON workflow
   * @param {Boolean} showActionFrames - Whether to include action frames as parent nodes
   * @returns {Object} Object containing nodes, edges, and actionFrames arrays
   */
  parseWorkflow(json, showActionFrames = true) {
    const { recipe } = json;
    if (!recipe) {
      throw new Error('Invalid JSON format: recipe not found');
    }

    const nodes = [];
    const edges = [];
    const nodeMap = {};
    const referenceLog = []; // For tracking references

    // Process connectors
    if (recipe.connectors && recipe.connectors.connector) {
      const connectorGroup = recipe.connectors.connector;
      this.processConnectorNodes(connectorGroup, nodes, nodeMap, referenceLog);
    }

    // Process write formats
    if (recipe.writeFormats && recipe.writeFormats.writeFormat) {
      const writeFormatGroup = recipe.writeFormats.writeFormat;
      this.processWriteFormatNodes(writeFormatGroup, nodes, nodeMap, referenceLog);
    }

    // Process read formats
    if (recipe.readFormats && recipe.readFormats.readFormat) {
      const readFormatGroup = recipe.readFormats.readFormat;
      this.processReadFormatNodes(readFormatGroup, nodes, nodeMap, referenceLog);
    }

    // Process tasks
    if (recipe.tasks && recipe.tasks.task) {
      const taskGroup = recipe.tasks.task;
      this.processTaskNodes(taskGroup, nodes, nodeMap, referenceLog);
    }

    // Process imports
    if (recipe.imports && recipe.imports.import) {
      const importGroup = recipe.imports.import;
      this.processImportNodes(importGroup, nodes, nodeMap, referenceLog);
    }

    // Process statements
    if (recipe.statements && recipe.statements.statement) {
      const statementGroup = recipe.statements.statement;
      this.processStatementNodes(statementGroup, nodes, nodeMap, referenceLog);
    }

    // Now that all nodes are processed, go through and create edges based on references
    this.createEdgesFromReferences(nodes, edges, nodeMap, referenceLog);
    // Calculate path depths for all nodes before identifying action frames
    this.calculateNodePathDepths(nodes, edges);

    // Identify action frames based on dataframes
    const actionFrames = this.identifyActionFrames(nodes, edges, showActionFrames);

    return { nodes, edges, actionFrames };
  }

  /**
   * Process connector nodes from the JSON
   * @param {Array|Object} connectorGroup - The connector data from JSON
   * @param {Array} nodes - Array to populate with node objects
   * @param {Object} nodeMap - Map to track node IDs
   * @param {Array} referenceLog - For tracking references
   */
  processConnectorNodes(connectorGroup, nodes, nodeMap, referenceLog) {
    const connectors = Array.isArray(connectorGroup) ? connectorGroup : [connectorGroup];

    connectors.forEach((connector, index) => {
      const id = connector['@id'] || connector.id;
      if (!id) return;

      const name =
        connector.name && connector.name['#text']
          ? connector.name['#text']
          : connector.name || `Connector ${index + 1}`;

      // Use type as is if it's a string or extract it from #text
      const type =
        connector.type && connector.type['#text']
          ? connector.type['#text']
          : connector.type || 'unknown';

      // Store all references
      const allReferences = this.extractAllReferences(connector, id, referenceLog);

      // Extract comment if available
      const comment = connector['@data-comment'] || '';

      // Determine if this is an output connector
      const isOutput =
        type.toLowerCase().includes('writer') ||
        id.toLowerCase().includes('writer') ||
        name.toLowerCase().includes('writer');
      // Add the node - use the exact type from connector
      nodes.push({
        id,
        type, // Using the exact type
        position: { x: 0, y: 0 }, // Will be set by layout engine
        data: {
          ...connector,
          label: name,
          name,
          type,
          details: `Type: ${type}`,
          nodeType: 'connector',
          comment,
          allReferences,
          isOutput,
          pathDepth: 0, // Initial path depth, will be calculated later
        },
      });

      // Add to node map for edge creation
      nodeMap[id] = {
        id,
        name,
        type: 'connector',
        index: nodes.length - 1,
        allReferences,
        isOutput,
      };
    });
  }

  /**
   * Process write format nodes from the JSON
   * @param {Array|Object} writeFormatGroup - The write format data from JSON
   * @param {Array} nodes - Array to populate with node objects
   * @param {Object} nodeMap - Map to track node IDs
   * @param {Array} referenceLog - For tracking references
   */
  processWriteFormatNodes(writeFormatGroup, nodes, nodeMap, referenceLog) {
    const writeFormats = Array.isArray(writeFormatGroup) ? writeFormatGroup : [writeFormatGroup];

    writeFormats.forEach((format, index) => {
      const id = format['@id'] || format.id;
      if (!id) return;

      const name =
        format.name && format.name['#text']
          ? format.name['#text']
          : format.name || `Write Format ${index + 1}`;

      const baseType =
        format.type && format.type['#text'] ? format.type['#text'] : format.type || 'unknown';

      // Add 'Writer' suffix to type
      const type = `${baseType}Writer`;

      // Store all references
      const allReferences = this.extractAllReferences(format, id, referenceLog);

      // Extract properties if available
      const properties = format.properties ? this.extractProperties(format.properties) : null;

      // Extract comment if available
      const comment = format['@data-comment'] || format['data-comment'] || '';

      // Add the node with Writer suffix in type
      nodes.push({
        id,
        type, // Using baseType + Writer
        nodeGroup: 'format',
        position: { x: 0, y: 0 }, // Will be set by layout engine
        data: {
          ...format,
          label: name,
          type: baseType,
          details: properties
            ? `Type: ${baseType}\nProperties: ${JSON.stringify(properties, null, 2)}`
            : `Type: ${baseType}`,
          nodeType: 'writeFormat',
          properties,
          comment,
          allReferences,
          pathDepth: 0, // Initial path depth, will be calculated later
        },
      });

      // Add to node map for edge creation
      nodeMap[id] = {
        id,
        name,
        type: 'writeFormat',
        index: nodes.length - 1,
        allReferences,
      };
    });
  }

  /**
   * Process read format nodes from the JSON
   * @param {Array|Object} readFormatGroup - The read format data from JSON
   * @param {Array} nodes - Array to populate with node objects
   * @param {Object} nodeMap - Map to track node IDs
   * @param {Array} referenceLog - For tracking references
   */
  processReadFormatNodes(readFormatGroup, nodes, nodeMap, referenceLog) {
    const readFormats = Array.isArray(readFormatGroup) ? readFormatGroup : [readFormatGroup];

    readFormats.forEach((readFormat, index) => {
      const id = readFormat['@id'] || readFormat.id;
      if (!id) return;

      const name =
        readFormat.name && readFormat.name['#text']
          ? readFormat.name['#text']
          : readFormat.name || `Read Format ${index + 1}`;

      const baseType =
        readFormat.type && readFormat.type['#text']
          ? readFormat.type['#text']
          : readFormat.type || 'unknown';

      // Add 'Reader' suffix to type
      const type = `${baseType}Reader`;

      // Store all references
      const allReferences = this.extractAllReferences(readFormat, id, referenceLog);

      // Extract properties if available
      const properties = readFormat.properties
        ? this.extractProperties(readFormat.properties)
        : null;

      // Extract comment if available
      const comment = readFormat['@data-comment'] || readFormat['data-comment'] || '';

      // Add the node with Reader suffix in type
      nodes.push({
        id,
        type, // Using baseType + Reader
        nodeGroup: 'format',
        position: { x: 0, y: 0 }, // Will be set by layout engine
        data: {
          ...readFormat,
          label: name,
          type: baseType,
          details: properties
            ? `Type: ${baseType}\nProperties: ${JSON.stringify(properties, null, 2)}`
            : `Type: ${baseType}`,
          nodeType: 'readFormat',
          properties,
          comment,
          allReferences,
          pathDepth: 0, // Initial path depth, will be calculated later
        },
      });

      // Add to node map for edge creation
      nodeMap[id] = {
        id,
        name,
        type: 'readFormat',
        index: nodes.length - 1,
        allReferences,
      };
    });
  }

  /**
   * Process task nodes from the JSON
   * @param {Array|Object} taskGroup - The task data from JSON
   * @param {Array} nodes - Array to populate with node objects
   * @param {Object} nodeMap - Map to track node IDs
   * @param {Array} referenceLog - For tracking references
   */
  processTaskNodes(taskGroup, nodes, nodeMap, referenceLog) {
    const tasks = Array.isArray(taskGroup) ? taskGroup : [taskGroup];

    tasks.forEach((task, index) => {
      const id = task['@id'] || task.id;
      if (!id) return;

      const name =
        task.name && task.name['#text'] ? task.name['#text'] : task.name || `Task ${index + 1}`;

      const baseType =
        task.type && task.type['#text'] ? task.type['#text'] : task.type || 'unknown';

      const functionName =
        task.function && task.function['#text']
          ? task.function['#text']
          : task.function || 'unknown';

      // Combine type and function name for the node type
      const type = `${baseType}${functionName}`;

      // Store all references
      const allReferences = this.extractAllReferences(task, id, referenceLog);

      // Extract parameters if available
      const parameters = task.parameters ? this.extractProperties(task.parameters) : null;

      // Extract comment if available
      const comment = task['@data-comment'] || task['data-comment'] || '';

      // Build the data object
      const nodeData = {
        ...task,
        label: name,
        type: baseType,
        function: functionName,
        details: `Type: ${baseType}\nFunction: ${functionName}\nParameters: ${
          parameters ? JSON.stringify(parameters, null, 2) : 'None'
        }`,
        nodeType: 'task',
        comment,
        allReferences,
        pathDepth: 0, // Initial path depth, will be calculated later
      };

      // Only include parameters if they exist and are not empty
      if (parameters && Object.keys(parameters).length > 0) {
        nodeData.parameters = parameters;
      }

      // Add the node with type + function as type
      nodes.push({
        id,
        type, // Using type + function name
        nodeGroup: 'task',
        position: { x: 0, y: 0 }, // Will be set by layout engine
        data: nodeData,
      });

      // Add to node map for edge creation
      nodeMap[id] = {
        id,
        name,
        type: 'task',
        index: nodes.length - 1,
        allReferences,
      };
    });
  }

  /**
   * Process import nodes from the JSON
   * @param {Array|Object} importGroup - The import data from JSON
   * @param {Array} nodes - Array to populate with node objects
   * @param {Object} nodeMap - Map to track node IDs
   * @param {Array} referenceLog - For tracking references
   */
  processImportNodes(importGroup, nodes, nodeMap, referenceLog) {
    const imports = Array.isArray(importGroup) ? importGroup : [importGroup];

    imports.forEach((importNode, index) => {
      const id = importNode['@id'] || importNode.id;
      if (!id) return;

      const name =
        importNode.name && importNode.name['#text']
          ? importNode.name['#text']
          : importNode.name || `Import ${index + 1}`;

      const type =
        importNode.type && importNode.type['#text']
          ? importNode.type['#text']
          : importNode.type || 'unknown';

      // Store all references
      const allReferences = this.extractAllReferences(importNode, id, referenceLog);

      // Extract properties if available
      const properties = importNode.properties
        ? this.extractProperties(importNode.properties)
        : null;

      // Extract comment if available
      const comment = importNode['@data-comment'] || importNode['data-comment'] || '';

      // Add the node
      nodes.push({
        id,
        type, // Using exact type
        nodeGroup: 'import',
        position: { x: 0, y: 0 }, // Will be set by layout engine
        data: {
          ...importNode,
          label: name,
          type,
          details: properties
            ? `Type: ${type}\nProperties: ${JSON.stringify(properties, null, 2)}`
            : `Type: ${type}`,
          nodeType: 'import',
          properties,
          comment,
          allReferences,
          pathDepth: 0, // Initial path depth, will be calculated later
        },
      });

      // Add to node map for edge creation
      nodeMap[id] = {
        id,
        name,
        type: 'import',
        index: nodes.length - 1,
        allReferences,
      };
    });
  }

  /**
   * Process statement nodes from the JSON
   * @param {Array|Object} statementGroup - The statement data from JSON
   * @param {Array} nodes - Array to populate with node objects
   * @param {Object} nodeMap - Map to track node IDs
   * @param {Array} referenceLog - For tracking references
   */
  processStatementNodes(statementGroup, nodes, nodeMap, referenceLog) {
    const statements = Array.isArray(statementGroup) ? statementGroup : [statementGroup];

    statements.forEach((statement, index) => {
      const id = statement['@id'] || statement.id;
      if (!id) return;

      const action = statement.action || 'unknown';
      const command = statement.command || '';
      const dataFrame = statement['data-frame'] || '';
      const query = statement.query || '';
      const dataType = statement['@data-type'] || statement['data-type'] || '';

      // Determine the node type based on data-type and action
      let nodeType = action; // Default to the action

      // For command type, append "Command" to the action
      if (dataType === 'command') {
        nodeType = `${action}Command`;
      }
      // For dataframes, use the action directly (this matches the second file's logic)
      else if (dataType === 'dataframe') {
        nodeType = action;
      }
      // For all other cases, use 'default' as specified in second file
      else {
        nodeType = 'default';
      }

      // Store all references
      const allReferences = this.extractAllReferences(statement, id, referenceLog);

      // Get comment if available
      const comment = statement['@data-comment'] || statement['data-comment'] || '';

      // Prepare node label
      let label = `${action} ${index + 1}`;
      if (dataFrame) {
        label = `${action}: ${dataFrame}`;
      }

      // Determine if this is an output operation
      const isOutput =
        action.toLowerCase() === 'write' ||
        action.toLowerCase() === 'update' ||
        action.toLowerCase() === 'insert';

      // Add the node with specific type based on data-type
      nodes.push({
        id,
        type: nodeType, // Using determined node type
        position: { x: 0, y: 0 }, // Will be set by layout engine
        data: {
          ...statement,
          label,
          details: command,
          command,
          comment,
          nodeType: 'statement',
          query,
          dataFrame,
          'data-frame': dataFrame,
          dataType,
          action,
          allReferences,
          isOutput,
          pathDepth: 0, // Initial path depth, will be calculated later
        },
      });

      // Add to node map for edge creation
      nodeMap[id] = {
        id,
        name: action,
        type: 'statement',
        index: nodes.length - 1,
        allReferences,
        isOutput,
      };
    });
  }

  /**
   * Extract all references from a node regardless of type
   * @param {Object} node - The node object from JSON
   * @param {String} nodeId - The ID of the current node (for logging)
   * @param {Array} referenceLog - For reference logging
   * @returns {Array} Array of all references
   */
  extractAllReferences(node, nodeId, referenceLog) {
    const references = [];

    // Handle both @ prefixed and non-prefixed attributes
    const refAttributePairs = [
      ['@ref-connector-id', 'ref-connector-id'],
      ['@ref-statement-id', 'ref-statement-id'],
      ['@ref-dataframe-id', 'ref-dataframe-id'],
      ['@ref-task-id', 'ref-task-id'],
      ['@ref-readFormat-id', 'ref-readFormat-id'],
      ['@ref-writeFormat-id', 'ref-writeFormat-id'],
      ['@ref-import-id', 'ref-import-id'],
    ];

    // Process each reference type
    refAttributePairs.forEach(([attrName, plainAttrName]) => {
      const value = node[attrName] || node[plainAttrName];

      if (value) {
        // Split by comma and trim each reference ID
        const refIds = value.split(',').map((id) => id.trim());

        // Add each reference to the list with its type
        refIds.forEach((id) => {
          const getRefType = () => {
            if (attrName.includes('connector')) return 'connector';
            if (attrName.includes('statement')) return 'statement';
            if (attrName.includes('dataframe')) return 'dataframe';
            if (attrName.includes('task')) return 'task';
            if (attrName.includes('readFormat')) return 'readFormat';
            if (attrName.includes('writeFormat')) return 'writeFormat';
            if (attrName.includes('import')) return 'import';
            return 'unknown';
          };
          const refType = getRefType();

          references.push({
            id,
            type: refType,
          });

          // Log the reference
          if (referenceLog) {
            referenceLog.push({
              sourceId: nodeId,
              targetId: id,
              refType,
            });
          }
        });
      }
    });

    return references;
  }

  calculateNodePathDepths(nodes, edges) {
    // Handle empty recipes or recipes with no valid nodes
    if (!nodes || nodes.length === 0) {
      logger.warn('No valid nodes found in recipe. Nodes must have @id attributes.');
      return;
    }

    // Create adjacency list for graph
    const graph = {};
    const nodeMap = new Map();

    // Initialize graph and node lookup
    nodes.forEach((node) => {
      graph[node.id] = {
        successors: [],
        predecessors: [],
      };
      nodeMap.set(node.id, node);
    });

    // Build graph connections from edges and detect self-references
    const selfReferences = new Set();
    edges.forEach((edge) => {
      // Check for self-reference
      if (edge.source === edge.target) {
        selfReferences.add(edge.source);
        // Skip adding this edge to break the cycle
        return;
      }

      if (graph[edge.source]) {
        graph[edge.source].successors.push(edge.target);
      }
      if (graph[edge.target]) {
        graph[edge.target].predecessors.push(edge.source);
      }
    });

    // Log any self-references found
    if (selfReferences.size > 0) {
      const selfRefNodes = [];
      selfReferences.forEach((nodeId) => {
        const node = nodeMap.get(nodeId);
        if (node) {
          selfRefNodes.push({
            id: nodeId,
            name: node.data.name || node.data.label || nodeId,
            type: node.data.nodeType,
          });
        }
      });

      // Detailed console warning
      logger.warn('Self-referencing nodes detected:', {
        nodes: selfRefNodes.map((node) => `${node.type}: "${node.name}" (ID: ${node.id})`),
        fix: 'Remove self-references to prevent infinite loops',
      });

      // Minimal UI alert
      this.showSnackbar('Self-referencing nodes detected. Check console for details.', 'error');
    }

    // Find root nodes (no incoming edges)
    const rootNodes = nodes
      .filter(
        (node) => !edges.some((edge) => edge.target === node.id && edge.source !== edge.target)
      )
      .map((node) => node.id);

    // Set depth 0 for root nodes
    rootNodes.forEach((id) => {
      const node = nodeMap.get(id);
      if (node) {
        node.data.pathDepth = 0;
      }
    });

    // Perform BFS with a safety counter to prevent infinite loops
    const queue = [...rootNodes.map((id) => ({ id, depth: 0 }))];
    const _visited = new Set(rootNodes);

    // Add a safety counter
    let iterationCount = 0;
    const MAX_ITERATIONS = Math.max(100, nodes.length * 10); // Ensure minimum of 100 iterations

    while (queue.length > 0 && iterationCount < MAX_ITERATIONS) {
      iterationCount++;
      const { id, depth } = queue.shift();
      const node = nodeMap.get(id);

      if (node) {
        // Update path depth to maximum seen path
        const oldDepth = node.data.pathDepth || 0;
        node.data.pathDepth = Math.max(oldDepth, depth);

        // Add successors to queue if they haven't been visited at this depth
        graph[id].successors.forEach((successorId) => {
          // Skip self-references
          if (successorId === id || selfReferences.has(successorId)) {
            return;
          }

          const successorNode = nodeMap.get(successorId);
          if (successorNode) {
            const newDepth = depth + 1;
            const currentDepth = successorNode.data.pathDepth || 0;

            // Only process if new depth is greater
            if (newDepth > currentDepth) {
              queue.push({ id: successorId, depth: newDepth });
            }
          }
        });
      }
    }

    // Check if we hit the safety limit
    if (iterationCount >= MAX_ITERATIONS) {
      // Try to identify nodes involved in cycles
      const nodesInCycles = [];
      const processedMultipleTimes = new Map();

      // Track which nodes were processed multiple times (likely in cycles)
      queue.forEach(({ id }) => {
        const count = processedMultipleTimes.get(id) || 0;
        processedMultipleTimes.set(id, count + 1);
      });

      // Find nodes that appear multiple times in the remaining queue
      processedMultipleTimes.forEach((count, nodeId) => {
        if (count > 1) {
          const node = nodeMap.get(nodeId);
          if (node) {
            nodesInCycles.push({
              id: nodeId,
              name: node.data.name || node.data.label || nodeId,
              type: node.data.nodeType,
            });
          }
        }
      });

      // Log detailed error information to console
      let errorType = 'Recipe processing error';
      let errorDetails = {};

      if (nodes.length <= 1) {
        errorType = 'Invalid recipe structure';
        errorDetails = {
          issue: 'Empty or invalid recipe with no valid nodes',
          fix: 'Ensure all nodes have @id attributes',
        };
      } else if (nodesInCycles.length > 0) {
        errorType = 'Circular dependency detected';
        errorDetails = {
          issue: 'Nodes reference each other in a loop',
          problematicNodes: nodesInCycles.map(
            (node) => `${node.type}: "${node.name}" (ID: ${node.id})`
          ),
          fix: 'Remove circular references between nodes',
        };
      } else {
        errorDetails = {
          issue: 'Recipe structure too complex or contains unexpected patterns',
          iterations: iterationCount,
        };
      }

      logger.error(errorType, errorDetails);

      // Minimal UI alert
      let alertMessage = 'Recipe processing error';
      if (nodesInCycles.length > 0) {
        alertMessage = 'Circular dependency detected';
      } else if (nodes.length <= 1) {
        alertMessage = 'Invalid recipe structure';
      }

      this.showSnackbar(`${alertMessage}. Check console for details.`, 'error');
    }

    // Handle any unassigned nodes (disconnected or in cycles)
    nodes.forEach((node) => {
      if (node.data.pathDepth === undefined) {
        node.data.pathDepth = 0;
      }
    });
  }

  /**
   * Create edges between nodes based on references
   * @param {Array} nodes - Array of node objects
   * @param {Array} edges - Array to populate with edge objects
   * @param {Object} nodeMap - Map to track node IDs
   * @param {Array} referenceLog - Reference log for debugging
   */
  createEdgesFromReferences(nodes, edges, nodeMap, _referenceLog = []) {
    // Process each node
    nodes.forEach((node) => {
      const { allReferences } = node.data;
      if (!allReferences || allReferences.length === 0) return;

      // Create an edge from each reference to this node
      allReferences.forEach((reference) => {
        // Skip if the referenced node doesn't exist
        if (!nodeMap[reference.id]) {
          return;
        }

        // Determine edge style based on reference type
        let edgeStyle = { strokeWidth: 2 };
        switch (reference.type) {
          case 'connector':
            edgeStyle = { stroke: '#2E86C1', strokeWidth: 2 };
            break;
          case 'statement':
            edgeStyle = { stroke: '#E74C3C', strokeWidth: 2 };
            break;
          case 'dataframe':
            edgeStyle = { stroke: '#8E44AD', strokeWidth: 2, strokeDasharray: '5 5' };
            break;
          case 'task':
            edgeStyle = { stroke: '#F39C12', strokeWidth: 2, strokeDasharray: '3 3' };
            break;
          case 'readFormat':
            edgeStyle = { stroke: '#16A085', strokeWidth: 2 };
            break;
          case 'writeFormat':
            edgeStyle = { stroke: '#D35400', strokeWidth: 2 };
            break;
          case 'import':
            edgeStyle = { stroke: '#7F8C8D', strokeWidth: 2, strokeDasharray: '4 4' };
            break;
        }

        // Create the edge from the reference to this node with explicit handle IDs
        edges.push({
          id: `e-${reference.id}-${node.id}`,
          source: reference.id, // From the referenced node (parent)
          target: node.id, // To the current node (child)
          animated: true,
          markerEnd: {
            type: 'arrowclosed',
          },
          style: edgeStyle,
          data: { refType: reference.type },
        });
      });
    });

    return edges;
  }

  /**
   * Identify action frames based on dataframe patterns and create parent-child relationships
   * @param {Array} nodes - Array of node objects
   * @param {Array} edges - Array of edge objects
   * @param {Boolean} showActionFrames - Whether to include action frames as parent nodes
   * @returns {Object} Object containing identified action frames
   */
  identifyActionFrames(nodes, edges, showActionFrames = true) {
    const actionFrames = {};

    // Helper function to get strictly related nodes for a dataframe
    const getRelatedNodes = (dataframeName, rootNodeId, allNodes) => {
      const related = new Set([rootNodeId]);

      // First pass: Find direct references to the root node
      allNodes.forEach((node) => {
        if (node.data) {
          let isRelated = false;

          // Direct dataframe reference to root node
          if (node.data.allReferences) {
            isRelated = node.data.allReferences.some(
              (ref) => ref.id === rootNodeId && ref.type === 'dataframe'
            );
          }

          // Exact print command match for dataframe
          if (!isRelated && node.data.command) {
            const commandLower = node.data.command.toLowerCase();
            isRelated = commandLower === `print ${dataframeName.toLowerCase()}`;
          }

          // Direct query reference to dataframe
          if (!isRelated && node.data.query) {
            const queryLower = node.data.query.toLowerCase();
            isRelated =
              queryLower.includes(`from ${dataframeName.toLowerCase()}`) &&
              !queryLower.includes('count(');
          }

          // COUNT operation on this dataframe
          if (!isRelated && node.data.query && node.data.action === 'select') {
            const queryLower = node.data.query.toLowerCase();
            isRelated =
              queryLower.includes('count(') &&
              queryLower.includes(`from ${dataframeName.toLowerCase()}`);
          }

          if (isRelated) {
            related.add(node.id);
          }
        }
      });

      // Second pass: Find ONLY direct dataframe references to nodes we've already included
      // This is much more restrictive to prevent cascading through execution context
      let newNodesAdded = true;
      let passCount = 0;
      const MAX_PASSES = 2; // Limit cascade depth to prevent over-grouping

      while (newNodesAdded && passCount < MAX_PASSES) {
        newNodesAdded = false;
        passCount++;

        allNodes.forEach((node) => {
          if (node.data && !related.has(node.id)) {
            let isRelated = false;

            // Only include nodes with DIRECT dataframe references to already included nodes
            if (node.data.allReferences) {
              isRelated = node.data.allReferences.some(
                (ref) => related.has(ref.id) && ref.type === 'dataframe'
              );
            }

            // Print commands for derived dataframes created by nodes we've already included
            if (!isRelated && node.data.command) {
              const commandLower = node.data.command.toLowerCase();
              const relatedNodes = Array.from(related)
                .map((id) => allNodes.find((n) => n.id === id))
                .filter(Boolean);

              isRelated = relatedNodes.some(
                (relNode) =>
                  relNode.data &&
                  relNode.data['data-frame'] &&
                  commandLower === `print ${relNode.data['data-frame'].toLowerCase()}`
              );
            }

            // Count operations on dataframes created by nodes we've already included
            if (!isRelated && node.data.query && node.data.action === 'select') {
              const queryLower = node.data.query.toLowerCase();
              if (queryLower.includes('count(')) {
                const relatedNodes = Array.from(related)
                  .map((id) => allNodes.find((n) => n.id === id))
                  .filter(Boolean);

                isRelated = relatedNodes.some(
                  (relNode) =>
                    relNode.data &&
                    relNode.data['data-frame'] &&
                    queryLower.includes(`from ${relNode.data['data-frame'].toLowerCase()}`)
                );
              }
            }

            if (isRelated) {
              related.add(node.id);
              newNodesAdded = true;
            }
          }
        });
      }

      return Array.from(related);
    };

    // Step 1: Identify primary dataframe nodes
    const dataframeNodes = nodes.filter((node) => {
      if (!node.data || node.data.nodeType !== 'statement') return false;

      // Use both explicit type and node category to identify dataframe operations
      const isDataframeOperation =
        node.data.action === 'select' ||
        node.data.action === 'insert' ||
        node.data.action === 'update';

      const hasDataframe = Boolean(node.data['data-frame']);

      // Exclude count operations as primary nodes
      const isCountOperation = node.data.query && node.data.query.toLowerCase().includes('count(');

      // Exclude INSERT operations into executionContext as they're utility operations
      const isExecutionContextInsert =
        node.data.action === 'insert' &&
        node.data.command &&
        node.data.command.toLowerCase().includes('executioncontext');

      return isDataframeOperation && hasDataframe && !isCountOperation && !isExecutionContextInsert;
    });

    // Step 2: Create action frames
    dataframeNodes.forEach((dfNode) => {
      const frameId = `frame-${dfNode.id}`;
      const dataframeName = dfNode.data['data-frame'];

      // Get all related nodes using strict criteria
      const relatedNodes = getRelatedNodes(dataframeName, dfNode.id, nodes);

      // Only create frame if we have related nodes (more than just the root)
      if (relatedNodes.length > 1) {
        // Create frame metadata
        actionFrames[frameId] = {
          id: frameId,
          rootNode: dfNode.id,
          'data-frame': dataframeName,
          nodes: relatedNodes,
          label: dataframeName,
          comment: dfNode.data.comment || '',
          pathDepth: dfNode.data.pathDepth || 0,
        };

        if (showActionFrames) {
          // Create parent node - minimal size to start, will be adjusted later
          const parentNode = {
            id: frameId,
            type: 'actionFrame',
            position: { x: 0, y: 0 },
            style: {
              width: 180,
              height: 120,
              zIndex: -1,
              background: 'rgba(240, 240, 240, 0.25)',
              border: '1px solid #ddd',
            },
            data: {
              label: dataframeName,
              comment: dfNode.data.comment || '',
              dataFrame: dataframeName,
              childrenCount: relatedNodes.length,
              nodeType: 'actionFrame',
              pathDepth: dfNode.data.pathDepth || 0,
            },
            pathDepth: dfNode.data.pathDepth || 0,
            className: 'action-frame-node',
            zIndex: -1,
          };

          nodes.push(parentNode);

          // Set initial position for each child node relative to parent
          relatedNodes.forEach((nodeId) => {
            const node = nodes.find((n) => n.id === nodeId);
            if (node) {
              node.data.actionFrame = frameId;
              node.parentNode = frameId;
              node.extent = 'parent';
              node.expandParent = true;
              node.zIndex = 1;

              // Set positions relative to parent (will be adjusted by layout engine)
              node.position = { x: 50, y: 50 };
            }
          });
        } else {
          // Just tag nodes with frame ID when not showing frames
          relatedNodes.forEach((nodeId) => {
            const node = nodes.find((n) => n.id === nodeId);
            if (node) {
              node.data.actionFrame = frameId;
            }
          });
        }
      }
    });

    return actionFrames;
  }

  /**
   * Extract properties object from connector or format properties
   * @param {Object} propertiesObject - The properties section from JSON
   * @returns {Object} Extracted properties as a clean object
   */
  extractProperties(propertiesObject) {
    if (!propertiesObject) return null;

    // If it's already a simple object with key-value pairs
    if (typeof propertiesObject === 'object' && !('@id' in propertiesObject)) {
      return propertiesObject;
    }

    // For more complex property objects with @id and other metadata
    const properties = {};

    // Extract all properties that aren't metadata (don't start with @)
    Object.keys(propertiesObject).forEach((key) => {
      if (!key.startsWith('@')) {
        properties[key] = propertiesObject[key];
      }
    });

    return properties;
  }
}

export default WorkflowParser;
